import {
  Archive,
  CalendarDays,
  Check,
  ChevronDown,
  Download,
  FolderOpen,
  Info,
  Pencil,
  Plus,
  Save,
  SlidersHorizontal,
  Trash2,
  X,
} from 'lucide-react';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { billingService, installationService } from '../../services/api';
import { installationStatusLabel } from '../../utils/installationStatuses';

const VACATION_TYPE_LABELS = {
  UW: 'Urlop wypoczynkowy',
  NN: 'Nieusprawiedliwiona nieobecność',
  'UNŻ': 'Urlop na żądanie',
  L4: 'Chorobowe',
  SW: 'Służba wojskowa',
  ST: 'Stan szczególny',
  UB: 'Urlop bezpłatny',
};

function apiError(e, fallback) {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (Array.isArray(detail)) {
    return detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
  }
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}

function formatInstallationDate(item) {
  if (!item.planned_start_date) return '—';
  if (item.planned_start_date === item.planned_end_date || !item.planned_end_date) {
    return item.planned_start_date;
  }
  return `${item.planned_start_date} – ${item.planned_end_date}`;
}

function formatPolishDate(value) {
  if (!value) return '—';
  const [year, month, day] = value.split('-');
  return `${day}.${month}.${year}`;
}

function formatSavedAt(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat('pl-PL', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatMoney(value) {
  return `${Number(value || 0).toFixed(2)} PLN`;
}

export function pzlSettlementModeLabel(mode) {
  if (mode === 'per_person') return 'Stała kwota dla każdej osoby';
  if (mode === 'equal_split') return 'Łączna kwota dzielona po równo';
  return 'SUMA USL – podział według wag';
}

function isPzlItem(item) {
  return Boolean(item?.is_pzl || (item?.order_number || '').toUpperCase().includes('PZL'));
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString('pl-PL', { maximumFractionDigits: 2 });
}

function formatVacationDates(vacation) {
  const dates = vacation.paid_dates || [];
  if (dates.length === 0) return '—';
  if (dates.length === 1) return dates[0];
  return `${dates[0]} – ${dates[dates.length - 1]}`;
}

function installationAssignedDayCount(installation) {
  const calculationDays = installation.calculation_days || [];
  const assignedDates = new Set(
    calculationDays
      .map((day) => day.assigned_date)
      .filter(Boolean)
  );
  if (assignedDates.size > 0) return assignedDates.size;
  if (calculationDays.length > 0) return calculationDays.length;
  return installation.total_assignment_days || '—';
}

function formatSavedInstallerWeights(installer) {
  const weights = [...new Set(
    (installer.installations || [])
      .map((installation) => installation.position_weight)
      .filter((weight) => weight !== null && weight !== undefined)
      .map(Number)
  )];
  return weights.length > 0 ? weights.map(formatNumber).join(' / ') : '—';
}

function installationSortDate(installation) {
  const calculationDates = (installation.calculation_days || [])
    .map((day) => day.assigned_date)
    .filter(Boolean)
    .sort();
  return calculationDates[0]
    || installation.planned_start_date
    || installation.planned_end_date
    || '9999-12-31';
}

function compareInstallationsByDate(left, right) {
  const dateComparison = installationSortDate(left).localeCompare(
    installationSortDate(right)
  );
  if (dateComparison !== 0) return dateComparison;
  return String(left.order_number || '').localeCompare(
    String(right.order_number || ''),
    'pl'
  );
}

export function PositionRatesSection({ variant = 'all-rates' }) {
  const [rates, setRates] = useState([]);
  const [newRate, setNewRate] = useState({ position_name: '', weight_pl: '', weight_de: '' });
  const [isCustomPosition, setIsCustomPosition] = useState(false);
  const [editRate, setEditRate] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editVals, setEditVals] = useState({ weight_pl: '', weight_de: '' });
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const isOfficialPositionsVariant = variant === 'official-positions';
  const contentId = `position-rates-content-${variant}`;

  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [billingService.listPositionRates()];
      requests.push(
        isOfficialPositionsVariant
          ? installationService.listPositions()
          : installationService.listInstallers()
      );
      const [rateData, positionSource] = await Promise.all(requests);
      setRates(rateData);
      setPositions(
        isOfficialPositionsVariant
          ? positionSource.map((p) => p.name)
          : [...new Set(positionSource.map((i) => i.position).filter(Boolean))]
      );
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [isOfficialPositionsVariant]);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  const rateMap = Object.fromEntries(rates.map((r) => [r.position_name, r]));

  async function handleCreate() {
    if (!newRate.position_name || newRate.weight_pl === '' || newRate.weight_de === '') {
      setError('Wypełnij stanowisko oraz obie wagi (PL i DE)');
      return;
    }
    try {
      await billingService.createPositionRate({
        position_name: newRate.position_name,
        weight_pl: parseFloat(newRate.weight_pl),
        weight_de: parseFloat(newRate.weight_de),
      });
      setNewRate({ position_name: '', weight_pl: '', weight_de: '' });
      setIsCustomPosition(false);
      setError('');
      fetchRates();
    } catch (e) {
      setError(apiError(e, 'Błąd zapisu'));
    }
  }

  async function handleSave() {
    try {
      await billingService.updatePositionRate(editRate.id, {
        position_name: editRate.position_name,
        weight_pl: parseFloat(editRate.weight_pl),
        weight_de: parseFloat(editRate.weight_de),
      });
      setEditRate(null);
      fetchRates();
    } catch (e) {
      setError(apiError(e, 'Błąd zapisu'));
    }
  }

  async function handleSaveRow(positionName) {
    const weight_pl = parseFloat(editVals.weight_pl);
    const weight_de = parseFloat(editVals.weight_de);
    if (isNaN(weight_pl) || isNaN(weight_de)) {
      setError('Uzupełnij obie wagi dla stanowiska: ' + positionName);
      return;
    }

    setSaving(true);
    try {
      const existing = rateMap[positionName];
      if (existing) {
        await billingService.updatePositionRate(existing.id, { weight_pl, weight_de });
      } else {
        await billingService.createPositionRate({ position_name: positionName, weight_pl, weight_de });
      }
      cancelEditRow();
      setError('');
      fetchRates();
    } catch (e) {
      setError(apiError(e, 'Błąd zapisu'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Usunąć stawkę?')) return;
    try {
      await billingService.deletePositionRate(id);
      fetchRates();
    } catch (e) {
      setError(apiError(e, 'Błąd usunięcia'));
    }
  }

  function startEditRow(positionName) {
    const rate = rateMap[positionName];
    setEditRow(positionName);
    setEditVals({
      weight_pl: rate ? String(rate.weight_pl) : '',
      weight_de: rate ? String(rate.weight_de) : '',
    });
  }

  function cancelEditRow() {
    setEditRow(null);
    setEditVals({ weight_pl: '', weight_de: '' });
  }

  return (
    <div className="billing-section position-rates-section">
      <button
        type="button"
        className="billing-section-toggle"
        onClick={() => setIsExpanded((expanded) => !expanded)}
        aria-expanded={isExpanded}
        aria-controls={contentId}
      >
        <span className="billing-section-heading">
          <span className="billing-section-icon" aria-hidden="true">
            <SlidersHorizontal size={22} />
          </span>
          <span>
            <span className="billing-section-title">Wagi stanowisk</span>
            <span className="billing-section-summary">
              {loading
                ? 'Ładowanie konfiguracji…'
                : `${rates.length} ${rates.length === 1 ? 'skonfigurowana stawka' : 'skonfigurowanych stawek'}`}
            </span>
          </span>
        </span>
        <span className="billing-section-toggle-action">
          <span>{isExpanded ? 'Zwiń' : 'Rozwiń'}</span>
          <span className={`billing-section-chevron${isExpanded ? ' is-expanded' : ''}`} aria-hidden="true">
            <ChevronDown size={20} />
          </span>
        </span>
      </button>

      {isExpanded && (
        <div id={contentId} className="position-rates-content">
          <div className="position-rates-info">
            <Info size={18} aria-hidden="true" />
            <p>
              Wagi określają proporcjonalny udział stanowiska w wartości USL montażu.
              Udział procentowy jest obliczany względem sumy wag całego zespołu.
            </p>
          </div>

          {error && <div className="error-banner" onClick={() => setError('')}>{error}</div>}

          {!isOfficialPositionsVariant && (
            <div className="position-rate-editor">
              <div className="position-rate-editor-heading">
                <div>
                  <h4>Dodaj wagę stanowiska</h4>
                  <p>Wybierz stanowisko i określ jego wagę dla Polski oraz Niemiec.</p>
                </div>
              </div>
              <div className="add-rate-form">
                <div className="form-field">
                  <label>Stanowisko</label>
                  <select
                    value={isCustomPosition ? '__custom__' : newRate.position_name}
                    onChange={(e) => {
                      const useCustomPosition = e.target.value === '__custom__';
                      setIsCustomPosition(useCustomPosition);
                      setNewRate({
                        ...newRate,
                        position_name: useCustomPosition ? '' : e.target.value,
                      });
                    }}
                  >
                    <option value="">-- Wybierz stanowisko --</option>
                    {positions.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                    <option value="__custom__">Inne (wpisz ręcznie)</option>
                  </select>
                </div>
                {isCustomPosition && (
                  <div className="form-field">
                    <label>Nazwa stanowiska</label>
                    <input
                      placeholder="np. Brygadzista"
                      value={newRate.position_name_custom || ''}
                      onChange={(e) =>
                        setNewRate({
                          ...newRate,
                          position_name_custom: e.target.value,
                          position_name: e.target.value,
                        })
                      }
                    />
                  </div>
                )}
                <div className="form-field">
                  <label>Waga PL</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    placeholder="np. 19"
                    value={newRate.weight_pl}
                    onChange={(e) => setNewRate({ ...newRate, weight_pl: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>Waga DE</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    placeholder="np. 19"
                    value={newRate.weight_de}
                    onChange={(e) => setNewRate({ ...newRate, weight_de: e.target.value })}
                  />
                </div>
                <button className="btn-primary btn-add" onClick={handleCreate}>
                  <Plus size={17} aria-hidden="true" />
                  Przypisz wagę
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="loading-text">Ładowanie…</div>
          ) : isOfficialPositionsVariant ? (
            <OfficialPositionsRatesTable
              positions={positions}
              rateMap={rateMap}
              editRow={editRow}
              editVals={editVals}
              saving={saving}
              onEdit={startEditRow}
              onCancel={cancelEditRow}
              onSave={handleSaveRow}
              onEditValueChange={setEditVals}
            />
          ) : (
            <EditableRatesTable
              rates={rates}
              editRate={editRate}
              onEditRate={setEditRate}
              onSave={handleSave}
              onDelete={handleDelete}
            />
          )}
        </div>
      )}
    </div>
  );
}

export function PzlPriceListSection() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [pricing, setPricing] = useState({ settlement_mode: 'usl', amount: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const contentId = 'default-pzl-price-list-content';

  useEffect(() => {
    billingService.getPzlPriceList()
      .then((data) => setPricing({
        settlement_mode: data.settlement_mode,
        amount: String(data.amount ?? 0),
      }))
      .catch((requestError) => setError(apiError(requestError, 'Nie udało się pobrać cennika PZL')))
      .finally(() => setLoading(false));
  }, []);

  async function savePricing(event) {
    event.preventDefault();
    const amount = Number(String(pricing.amount).replace(',', '.'));
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Wprowadź kwotę większą od 0.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const saved = await billingService.updatePzlPriceList({
        settlement_mode: pricing.settlement_mode,
        amount: Number.isFinite(amount) ? amount : 0,
      });
      setPricing({ settlement_mode: saved.settlement_mode, amount: String(saved.amount) });
      setMessage('Cennik PZL został zapisany.');
    } catch (requestError) {
      setError(apiError(requestError, 'Nie udało się zapisać cennika PZL'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="billing-section position-rates-section pzl-price-list-section">
      <button
        type="button"
        className="billing-section-toggle"
        onClick={() => setIsExpanded((expanded) => !expanded)}
        aria-expanded={isExpanded}
        aria-controls={contentId}
      >
        <span className="billing-section-heading">
          <span className="billing-section-icon" aria-hidden="true">
            <SlidersHorizontal size={22} />
          </span>
          <span>
            <span className="billing-section-title">Cennik domyślny</span>
          </span>
        </span>
        <span className="billing-section-toggle-action">
          <span>{isExpanded ? 'Zwiń' : 'Rozwiń'}</span>
          <span className={`billing-section-chevron${isExpanded ? ' is-expanded' : ''}`} aria-hidden="true">
            <ChevronDown size={20} />
          </span>
        </span>
      </button>
      {isExpanded && (
      <div id={contentId} className="position-rates-content pzl-price-list-body">
        <div className="pzl-price-list-step-heading">
          <span>1</span>
          <div>
            <h4>Rozliczanie PZL</h4>
            <p>Wybierz domyślny sposób naliczania zleceń zawierających „PZL”.</p>
          </div>
        </div>
      {error && <div className="error-banner" onClick={() => setError('')}>{error}</div>}
      {message && <div className="pzl-price-list-success">{message}</div>}
      {loading ? (
        <div className="loading-text">Ładowanie cennika…</div>
      ) : (
        <form className="pzl-price-list-form" onSubmit={savePricing}>
          <div className="pzl-price-list-options">
            {[
              ['per_person', 'Kwota / osoba'],
              ['usl', 'SUMA USL'],
              ['equal_split', 'Pula po równo'],
            ].map(([mode, label]) => (
              <label className={`pzl-price-option${pricing.settlement_mode === mode ? ' is-selected' : ''}`} key={mode}>
                <input
                  type="radio"
                  name="pzl-price-mode"
                  value={mode}
                  checked={pricing.settlement_mode === mode}
                  onChange={() => setPricing((current) => ({ ...current, settlement_mode: mode }))}
                />
                <strong>{label}</strong>
              </label>
            ))}
          </div>
          <div className="pzl-price-list-actions">
            <label className="pzl-price-amount-label">
              <span>
                {pricing.settlement_mode === 'equal_split'
                  ? 'Łączna pula'
                  : pricing.settlement_mode === 'usl' ? 'Suma USL' : 'Cena'}
              </span>
              <span className="pzl-money-input">
                <input
                  type="text"
                  inputMode="decimal"
                  value={pricing.amount}
                  onChange={(event) => setPricing((current) => ({ ...current, amount: event.target.value }))}
                  disabled={saving}
                />
                <span>PLN</span>
              </span>
            </label>
            <span className="pzl-price-current-value">
              {pricing.settlement_mode === 'usl'
                ? `${formatMoney(pricing.amount)} zostanie rozliczone według wag stanowisk.`
                : `Aktualna cena: ${formatMoney(pricing.amount)}${pricing.settlement_mode === 'per_person' ? ' dla każdej osoby' : ' do podziału'}`}
            </span>
            <button type="submit" className="btn-primary" disabled={saving}>
              <Save size={16} aria-hidden="true" /> {saving ? 'Zapisywanie…' : 'Zapisz cennik'}
            </button>
          </div>
        </form>
      )}
      </div>
      )}
    </section>
  );
}

function EditableRatesTable({ rates, editRate, onEditRate, onSave, onDelete }) {
  return (
    <div className="position-rates-table-wrap">
      <div className="position-rates-table-heading">
        <div>
          <h4>Skonfigurowane wagi</h4>
          <p>Edytuj wartości używane podczas obliczania wynagrodzeń.</p>
        </div>
        <span className="position-rates-count">{rates.length}</span>
      </div>
      <div className="position-rates-table-scroll">
        <table className="data-table position-rates-table">
          <thead>
            <tr>
              <th>Stanowisko</th>
              <th>Waga PL</th>
              <th>Waga DE</th>
              <th className="position-rates-actions-heading">Akcje</th>
            </tr>
          </thead>
          <tbody>
            {rates.length === 0 && (
              <tr><td colSpan={4} className="no-data-cell">Nie skonfigurowano jeszcze wag stanowisk.</td></tr>
            )}
            {rates.map((rate) => {
              const isEditing = editRate?.id === rate.id;
              return (
                <tr key={rate.id} className={isEditing ? 'is-editing' : ''}>
                  <td>
                    {isEditing ? (
                      <input
                        className="position-name-input"
                        value={editRate.position_name}
                        onChange={(e) => onEditRate({ ...editRate, position_name: e.target.value })}
                      />
                    ) : <strong className="position-name">{rate.position_name}</strong>}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        className="weight-input"
                        type="number"
                        min="0"
                        step="0.1"
                        value={editRate.weight_pl}
                        onChange={(e) => onEditRate({ ...editRate, weight_pl: e.target.value })}
                      />
                    ) : <span className="weight-value">{rate.weight_pl}</span>}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        className="weight-input"
                        type="number"
                        min="0"
                        step="0.1"
                        value={editRate.weight_de}
                        onChange={(e) => onEditRate({ ...editRate, weight_de: e.target.value })}
                      />
                    ) : <span className="weight-value">{rate.weight_de}</span>}
                  </td>
                  <td className="actions-cell position-rate-actions">
                    {isEditing ? (
                      <>
                        <button className="btn-success position-action-btn" onClick={onSave}>
                          <Check size={15} aria-hidden="true" /> Zapisz
                        </button>
                        <button className="btn-secondary position-action-btn position-cancel-btn" onClick={() => onEditRate(null)}>
                          <X size={13} aria-hidden="true" /> Anuluj
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="btn-secondary position-action-btn position-edit-btn" onClick={() => onEditRate({ ...rate })}>
                          <Pencil size={13} aria-hidden="true" /> Edytuj
                        </button>
                        <button className="btn-danger-ghost position-action-btn" onClick={() => onDelete(rate.id)}>
                          <Trash2 size={15} aria-hidden="true" /> Usuń
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OfficialPositionsRatesTable({
  positions,
  rateMap,
  editRow,
  editVals,
  saving,
  onEdit,
  onCancel,
  onSave,
  onEditValueChange,
}) {
  return (
    <div className="position-rates-table-wrap">
      <div className="position-rates-table-heading">
        <div>
          <h4>Wagi według stanowisk</h4>
          <p>Brak wartości oznacza, że stanowisko nie ma jeszcze przypisanej wagi.</p>
        </div>
        <span className="position-rates-count">{positions.length}</span>
      </div>
      <div className="position-rates-table-scroll">
        <table className="data-table position-rates-table">
          <thead>
            <tr>
              <th>Stanowisko</th>
              <th>Waga PL</th>
              <th>Waga DE</th>
              <th className="position-rates-actions-heading">Akcje</th>
            </tr>
          </thead>
          <tbody>
            {positions.length === 0 && (
              <tr><td colSpan={4} className="no-data-cell">Brak zdefiniowanych stanowisk.</td></tr>
            )}
            {positions.map((positionName) => {
              const rate = rateMap[positionName];
              const isEditing = editRow === positionName;
              return (
                <tr key={positionName} className={isEditing ? 'is-editing' : ''}>
                  <td><strong className="position-name">{positionName}</strong></td>
                  <td>
                    {isEditing ? (
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="np. 19"
                        value={editVals.weight_pl}
                        onChange={(e) => onEditValueChange((v) => ({ ...v, weight_pl: e.target.value }))}
                        className="weight-input"
                        autoFocus
                      />
                    ) : (rate
                      ? <span className="weight-value">{rate.weight_pl}</span>
                      : <span className="no-rate">Nie ustawiono</span>)}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="np. 19"
                        value={editVals.weight_de}
                        onChange={(e) => onEditValueChange((v) => ({ ...v, weight_de: e.target.value }))}
                        className="weight-input"
                      />
                    ) : (rate
                      ? <span className="weight-value">{rate.weight_de}</span>
                      : <span className="no-rate">Nie ustawiono</span>)}
                  </td>
                  <td className="actions-cell position-rate-actions">
                    {isEditing ? (
                      <>
                        <button
                          className="btn-success position-action-btn"
                          onClick={() => onSave(positionName)}
                          disabled={saving}
                        >
                          <Check size={15} aria-hidden="true" /> {saving ? 'Zapisywanie…' : 'Zapisz'}
                        </button>
                        <button className="btn-secondary position-action-btn position-cancel-btn" onClick={onCancel}>
                          <X size={13} aria-hidden="true" /> Anuluj
                        </button>
                      </>
                    ) : (
                      <button className="btn-secondary position-action-btn position-edit-btn" onClick={() => onEdit(positionName)}>
                        <Pencil size={13} aria-hidden="true" /> Edytuj
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function EarningsSection({ onNavigateToPlanning }) {
  const [installers, setInstallers] = useState([]);
  const [filters, setFilters] = useState({
    installerId: '',
    startDate: '',
    endDate: '',
  });
  const [earnings, setEarnings] = useState(null);
  const [periodInstallations, setPeriodInstallations] = useState([]);
  const [periodInstallationsLoading, setPeriodInstallationsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(null);
  const [savedSettlements, setSavedSettlements] = useState([]);
  const [savedLoading, setSavedLoading] = useState(true);
  const [savingSettlement, setSavingSettlement] = useState(false);
  const [openingSettlement, setOpeningSettlement] = useState(null);
  const [deletingSettlement, setDeletingSettlement] = useState(null);
  const [settlementExportLoading, setSettlementExportLoading] = useState(null);
  const [activeSavedSettlement, setActiveSavedSettlement] = useState(null);
  const [settlementName, setSettlementName] = useState('');
  const [expandedReportRow, setExpandedReportRow] = useState(null);
  const [error, setError] = useState('');

  const loadSavedSettlements = useCallback(async () => {
    setSavedLoading(true);
    try {
      setSavedSettlements(await billingService.listSavedSettlements());
    } catch (e) {
      setError(apiError(e, 'Nie udało się pobrać zapisanych rozliczeń'));
    } finally {
      setSavedLoading(false);
    }
  }, []);

  useEffect(() => {
    installationService.listInstallers().then(setInstallers).catch(() => {});
    loadSavedSettlements();
  }, [loadSavedSettlements]);

  async function calculate({ preserveName = false } = {}) {
    setLoading(true);
    setError('');
    try {
      setPeriodInstallationsLoading(Boolean(filters.startDate && filters.endDate));
      const [data, allInstallations] = await Promise.all([
        billingService.getEarnings({
          installerId: filters.installerId || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
        }),
        filters.startDate && filters.endDate
          ? billingService.listPeriodInstallations(filters.startDate, filters.endDate)
          : Promise.resolve([]),
      ]);
      setEarnings(data);
      setPeriodInstallations(allInstallations);
      setActiveSavedSettlement(null);
      if (!preserveName) setSettlementName('');
    } catch (e) {
      setError(apiError(e, 'Błąd obliczania'));
    } finally {
      setLoading(false);
      setPeriodInstallationsLoading(false);
    }
  }

  async function saveSettlement() {
    if (!filters.startDate || !filters.endDate) {
      setError('Wybierz pełny zakres dat „Od” i „Do” przed zapisaniem rozliczenia');
      return;
    }
    if (!earnings || activeSavedSettlement) return;

    setSavingSettlement(true);
    setError('');
    try {
      const saved = await billingService.createSavedSettlement({
        name: settlementName.trim() || undefined,
        period_start: filters.startDate,
        period_end: filters.endDate,
        installer_id: filters.installerId ? Number(filters.installerId) : undefined,
      });
      setActiveSavedSettlement(saved);
      setEarnings(saved.earnings);
      setSettlementName(saved.name);
      await loadSavedSettlements();
    } catch (e) {
      setError(apiError(e, 'Nie udało się zapisać rozliczenia'));
    } finally {
      setSavingSettlement(false);
    }
  }

  async function openSavedSettlement(settlementId) {
    setOpeningSettlement(settlementId);
    setError('');
    try {
      const saved = await billingService.getSavedSettlement(settlementId);
      setActiveSavedSettlement(saved);
      setEarnings(saved.earnings);
      setSettlementName(saved.name);
      setFilters({
        installerId: saved.installer_id ? String(saved.installer_id) : '',
        startDate: saved.period_start,
        endDate: saved.period_end,
      });
      setPeriodInstallationsLoading(true);
      setPeriodInstallations(
        await billingService.listPeriodInstallations(
          saved.period_start,
          saved.period_end,
          saved.id
        )
      );
      await loadSavedSettlements();
    } catch (e) {
      setError(apiError(e, 'Nie udało się otworzyć zapisanego rozliczenia'));
    } finally {
      setOpeningSettlement(null);
      setPeriodInstallationsLoading(false);
    }
  }

  async function deleteSavedSettlement(saved) {
    if (!window.confirm(
      `Czy na pewno chcesz usunąć zapisane rozliczenie „${saved.name}”? Ta operacja jest nieodwracalna.`
    )) return;

    setDeletingSettlement(saved.id);
    setError('');
    try {
      await billingService.deleteSavedSettlement(saved.id);
      if (activeSavedSettlement?.id === saved.id) {
        setActiveSavedSettlement(null);
        setEarnings(null);
        setPeriodInstallations([]);
        setSettlementName('');
        setExpandedReportRow(null);
      }
      await loadSavedSettlements();
    } catch (e) {
      setError(apiError(e, 'Nie udało się usunąć zapisanego rozliczenia'));
    } finally {
      setDeletingSettlement(null);
    }
  }

  async function handlePdfExport(installerId) {
    setPdfLoading(installerId);
    try {
      if (activeSavedSettlement) {
        await billingService.exportSavedSettlementPdf(activeSavedSettlement.id, installerId);
      } else {
        await billingService.exportPdf(
          installerId,
          filters.startDate || undefined,
          filters.endDate || undefined
        );
      }
    } catch (e) {
      setError('Błąd generowania PDF: ' + apiError(e, e.message));
    } finally {
      setPdfLoading(null);
    }
  }

  async function handleSettlementExport(format) {
    if (!activeSavedSettlement) return;
    setSettlementExportLoading(format);
    setError('');
    try {
      await billingService.exportSavedSettlement(activeSavedSettlement.id, format);
    } catch (e) {
      setError(apiError(e, `Nie udało się wyeksportować rozliczenia do ${format.toUpperCase()}`));
    } finally {
      setSettlementExportLoading(null);
    }
  }

  async function addInstallationUsl(installationId, amount, description) {
    setError('');
    try {
      if (activeSavedSettlement) {
        const saved = await billingService.addSavedSettlementUsl(
          activeSavedSettlement.id,
          installationId,
          { amount, description }
        );
        setActiveSavedSettlement(saved);
        setEarnings(saved.earnings);
        setPeriodInstallations(
          await billingService.listPeriodInstallations(
            saved.period_start,
            saved.period_end,
            saved.id
          )
        );
        await loadSavedSettlements();
      } else {
        await billingService.addInstallationUsl(installationId, { amount, description });
        await calculate({ preserveName: true });
      }
    } catch (e) {
      setError(apiError(e, 'Nie udało się dodać USL do zlecenia'));
      throw e;
    }
  }

  async function updateInstallationUsl(installationId, itemId, amount, description) {
    setError('');
    try {
      if (activeSavedSettlement) {
        const saved = await billingService.updateSavedSettlementUsl(
          activeSavedSettlement.id,
          installationId,
          itemId,
          { amount, description }
        );
        setActiveSavedSettlement(saved);
        setEarnings(saved.earnings);
        setPeriodInstallations(
          await billingService.listPeriodInstallations(
            saved.period_start,
            saved.period_end,
            saved.id
          )
        );
        await loadSavedSettlements();
      } else {
        await billingService.updateInstallationUsl(
          installationId,
          itemId,
          { amount, description }
        );
        await calculate({ preserveName: true });
      }
    } catch (e) {
      setError(apiError(e, 'Nie udało się zaktualizować USL'));
      throw e;
    }
  }

  async function updateInstallationPzlPricing(installationId, payload) {
    setError('');
    try {
      if (activeSavedSettlement) {
        const saved = await billingService.updateSavedSettlementInstallationPzlPricing(
          activeSavedSettlement.id,
          installationId,
          payload
        );
        setActiveSavedSettlement(saved);
        setEarnings(saved.earnings);
        setPeriodInstallations(
          await billingService.listPeriodInstallations(
            saved.period_start,
            saved.period_end,
            saved.id
          )
        );
        await loadSavedSettlements();
      } else {
        await billingService.updateInstallationPzlPricing(installationId, payload);
        await calculate({ preserveName: true });
      }
    } catch (e) {
      setError(apiError(e, 'Nie udało się zmienić sposobu rozliczania PZL'));
      throw e;
    }
  }

  async function updateSavedSessionPzlPriceList(payload) {
    if (!activeSavedSettlement) return;
    setError('');
    try {
      const saved = await billingService.updateSavedSettlementPzlPriceList(
        activeSavedSettlement.id,
        payload
      );
      setActiveSavedSettlement(saved);
      setEarnings(saved.earnings);
      setPeriodInstallations(
        await billingService.listPeriodInstallations(
          saved.period_start,
          saved.period_end,
          saved.id
        )
      );
      await loadSavedSettlements();
    } catch (e) {
      setError(apiError(e, 'Nie udało się zapisać cennika tej sesji'));
      throw e;
    }
  }

  async function updateInstallationCalculation(installationId, payload) {
    if (!activeSavedSettlement) return;
    setError('');
    try {
      const saved = await billingService.updateSavedSettlementInstallationCalculation(
        activeSavedSettlement.id,
        installationId,
        payload
      );
      setActiveSavedSettlement(saved);
      setEarnings(saved.earnings);
      setPeriodInstallations(
        await billingService.listPeriodInstallations(
          saved.period_start,
          saved.period_end,
          saved.id
        )
      );
      await loadSavedSettlements();
    } catch (e) {
      setError(apiError(e, 'Nie udało się przeliczyć zlecenia'));
      throw e;
    }
  }

  async function updateTeamAdditionalAmount(installationId, payload) {
    if (!activeSavedSettlement) return;
    setError('');
    try {
      const saved = await billingService.updateSavedSettlementTeamAdditionalAmount(
        activeSavedSettlement.id,
        installationId,
        payload
      );
      setActiveSavedSettlement(saved);
      setEarnings(saved.earnings);
      setPeriodInstallations(
        await billingService.listPeriodInstallations(
          saved.period_start,
          saved.period_end,
          saved.id
        )
      );
      await loadSavedSettlements();
    } catch (e) {
      setError(apiError(e, 'Nie udało się zapisać dopłaty dla ekipy'));
      throw e;
    }
  }

  return (
    <div className="billing-section earnings-section">
      <div className="earnings-section-header">
        <div className="earnings-section-icon" aria-hidden="true">
          <CalendarDays size={22} />
        </div>
        <div>
          <h3>Rozliczenia</h3>
          <p>
            Oblicz wynagrodzenia i urlopy dla wybranego okresu oraz zachowaj wynik, wagi, cennik PZL i stawki z dnia zapisu.
          </p>
        </div>
      </div>

      {error && <div className="error-banner" onClick={() => setError('')}>{error}</div>}

      <div className="settlement-workspace">
        <div className="settlement-generator-card">
          <div className="settlement-card-heading">
            <div>
              <h4>Nowe rozliczenie</h4>
              <p>Wskaż pracownika lub rozlicz cały zespół w wybranym okresie.</p>
            </div>
          </div>
          <div className="earnings-filters">
            <label>Pracownik
              <select
                value={filters.installerId}
                onChange={(e) => setFilters({ ...filters, installerId: e.target.value })}
              >
                <option value="">Wszyscy pracownicy</option>
                {installers.map((i) => (
                  <option key={i.id} value={i.id}>{i.first_name} {i.last_name} ({i.position})</option>
                ))}
              </select>
            </label>
            <label>Od
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </label>
            <label>Do
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </label>
            <button
              className="btn-primary settlement-calculate-btn"
              onClick={() => calculate()}
              disabled={loading}
            >
              {loading ? 'Obliczanie…' : 'Oblicz rozliczenie'}
            </button>
          </div>
        </div>

        <div className="saved-settlements-card">
          <div className="settlement-card-heading">
            <div>
              <h4><Archive size={17} aria-hidden="true" /> Zapisane rozliczenia</h4>
              <p>Zapisane wyniki zachowują wagi, sposoby rozliczania PZL i pełne cenniki urlopowe, a wartości USL można później uzupełnić.</p>
            </div>
            <span className="saved-settlements-count">{savedSettlements.length}</span>
          </div>
          <div className="saved-settlements-list">
            {savedLoading ? (
              <div className="saved-settlements-empty">Ładowanie zapisanych rozliczeń…</div>
            ) : savedSettlements.length === 0 ? (
              <div className="saved-settlements-empty">Nie zapisano jeszcze żadnego rozliczenia.</div>
            ) : savedSettlements.map((saved) => (
              <div className="saved-settlement-row" key={saved.id}>
                <button
                  type="button"
                  className={`saved-settlement-item${activeSavedSettlement?.id === saved.id ? ' is-active' : ''}`}
                  onClick={() => openSavedSettlement(saved.id)}
                  disabled={openingSettlement === saved.id || deletingSettlement === saved.id}
                >
                  <span className="saved-settlement-main">
                    <strong>{saved.name}</strong>
                    <span>{formatPolishDate(saved.period_start)} – {formatPolishDate(saved.period_end)}</span>
                  </span>
                  <span className="saved-settlement-meta">
                    <span>{saved.total_earned.toFixed(2)} PLN</span>
                    <FolderOpen size={16} aria-hidden="true" />
                  </span>
                </button>
                <button
                  type="button"
                  className="saved-settlement-delete"
                  aria-label={`Usuń zapisane rozliczenie ${saved.name}`}
                  title="Usuń zapisane rozliczenie"
                  onClick={() => deleteSavedSettlement(saved)}
                  disabled={deletingSettlement === saved.id || openingSettlement === saved.id}
                >
                  <Trash2 size={16} aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {earnings && (
        <div className="earnings-results">
          {activeSavedSettlement ? (
            <div className="saved-settlement-banner">
              <div className="saved-settlement-banner-top">
                <div className="saved-settlement-banner-main">
                  <Archive size={20} aria-hidden="true" />
                  <div>
                    <span className="saved-settlement-label">Zapisane rozliczenie</span>
                    <strong>{activeSavedSettlement.name}</strong>
                    <span>
                      Utworzono {formatSavedAt(activeSavedSettlement.created_at)}
                      {activeSavedSettlement.created_by_name
                        ? ` przez ${activeSavedSettlement.created_by_name}`
                        : ''}
                    </span>
                  </div>
                </div>
                <div className="saved-settlement-export-actions" aria-label="Eksport całego rozliczenia">
                  {[
                    { format: 'pdf', label: 'PDF' },
                    { format: 'xlsx', label: 'XLSX' },
                    { format: 'xlsx-kadra', label: 'XLSX-Kadra' },
                  ].map(({ format, label }) => (
                    <button
                      type="button"
                      className="btn-primary btn-sm"
                      key={format}
                      onClick={() => handleSettlementExport(format)}
                      disabled={Boolean(settlementExportLoading)}
                    >
                      <Download size={14} aria-hidden="true" />
                      {settlementExportLoading === format ? 'Eksportowanie…' : label}
                    </button>
                  ))}
                </div>
              </div>
              <details className="saved-weights-details">
                <summary>
                  Wagi zapisane w rozliczeniu ({activeSavedSettlement.earnings.installers.length})
                </summary>
                <div className="saved-weights-table-wrap">
                  <table className="saved-weights-table">
                    <thead>
                      <tr>
                        <th>Użytkownik</th>
                        <th>Stanowisko</th>
                        <th>Waga</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...activeSavedSettlement.earnings.installers]
                        .sort((left, right) => left.full_name.localeCompare(right.full_name, 'pl'))
                        .map((installer) => (
                          <tr key={installer.installer_id}>
                            <td><strong>{installer.full_name}</strong></td>
                            <td>{installer.position || '—'}</td>
                            <td className="saved-installer-weight">
                              {formatSavedInstallerWeights(installer)}
                            </td>
                          </tr>
                        ))}
                      {activeSavedSettlement.earnings.installers.length === 0 && (
                        <tr>
                          <td colSpan={3} className="saved-weights-empty">
                            Brak pracowników w zapisanym rozliczeniu.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>
          ) : (
            <div className="settlement-save-bar">
              <div className="settlement-save-copy">
                <Save size={20} aria-hidden="true" />
                <div>
                  <strong>Zapisz ten wynik</strong>
                  <span>Zostanie zachowany wynik, aktualne wagi, ustawienia PZL oraz pełne cenniki urlopowe pracowników.</span>
                </div>
              </div>
              <div className="settlement-save-controls">
                <input
                  type="text"
                  maxLength={200}
                  placeholder="Nazwa rozliczenia (opcjonalnie)"
                  value={settlementName}
                  onChange={(e) => setSettlementName(e.target.value)}
                  disabled={savingSettlement}
                />
                <button
                  type="button"
                  className="btn-primary"
                  onClick={saveSettlement}
                  disabled={savingSettlement || !filters.startDate || !filters.endDate}
                >
                  <Save size={16} aria-hidden="true" />
                  {savingSettlement ? 'Zapisywanie…' : 'Zapisz rozliczenie'}
                </button>
              </div>
            </div>
          )}

          {activeSavedSettlement && (
            <SavedSessionPzlPriceList
              settlement={activeSavedSettlement}
              onSave={updateSavedSessionPzlPriceList}
            />
          )}

          {earnings.unassigned_installations?.length > 0 && (
            <UnassignedInstallationsWarning
              installations={earnings.unassigned_installations}
              onNavigateToPlanning={onNavigateToPlanning}
            />
          )}

          {(filters.startDate && filters.endDate) && (
            <PeriodInstallationsTable
              installations={periodInstallations}
              loading={periodInstallationsLoading}
              onAddUsl={addInstallationUsl}
              onUpdateUsl={updateInstallationUsl}
              onUpdatePzlPricing={updateInstallationPzlPricing}
              onUpdateCalculation={updateInstallationCalculation}
              isSnapshot={Boolean(activeSavedSettlement)}
            />
          )}

          {earnings.installers.length === 0 && earnings.unassigned_installations?.length === 0 ? (
            <div className="no-data-text">Brak danych rozliczeniowych dla wybranego zakresu.</div>
          ) : earnings.installers.length === 0 ? null : (
            earnings.installers.map((installer) => (
              <InstallerEarningsCard
                key={`${activeSavedSettlement?.id || 'current'}:${installer.installer_id}`}
                installer={installer}
                pdfLoading={pdfLoading}
                onPdfExport={handlePdfExport}
                expandedRow={expandedReportRow}
                onToggleRow={(rowKey) => setExpandedReportRow((current) => (
                  current === rowKey ? null : rowKey
                ))}
                onAddUsl={addInstallationUsl}
                onUpdatePzlPricing={updateInstallationPzlPricing}
                onUpdateCalculation={updateInstallationCalculation}
                onUpdateTeamAdditionalAmount={updateTeamAdditionalAmount}
                isSnapshot={Boolean(activeSavedSettlement)}
                periodInstallations={periodInstallations}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function SavedSessionPzlPriceList({ settlement, onSave }) {
  const sessionPricing = settlement.pzl_price_list || { settlement_mode: 'usl', amount: 0 };
  const [pricing, setPricing] = useState({
    settlement_mode: sessionPricing.settlement_mode,
    amount: String(sessionPricing.amount ?? 0),
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const contentId = `saved-session-pzl-price-list-${settlement.id}`;

  useEffect(() => {
    setPricing({
      settlement_mode: sessionPricing.settlement_mode,
      amount: String(sessionPricing.amount ?? 0),
    });
  }, [sessionPricing.settlement_mode, sessionPricing.amount]);

  useEffect(() => {
    setIsExpanded(false);
  }, [settlement.id]);

  async function submit(event) {
    event.preventDefault();
    const amount = Number(String(pricing.amount).replace(',', '.'));
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Wprowadź kwotę większą od 0.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await onSave({
        settlement_mode: pricing.settlement_mode,
        amount: Number.isFinite(amount) ? amount : 0,
      });
      setMessage('Cennik tej sesji został zapisany i PZL zostały przeliczone.');
    } catch (_) {
      setError('Nie udało się zapisać cennika sesji.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="saved-session-pzl-price-list">
      <button
        type="button"
        className="saved-session-pzl-toggle"
        onClick={() => setIsExpanded((expanded) => !expanded)}
        aria-expanded={isExpanded}
        aria-controls={contentId}
      >
        <span className="saved-session-pzl-heading-copy">
          <span className="calculation-eyebrow">Cennik zapisanej sesji</span>
          <strong>{settlement.name}</strong>
        </span>
        <span className="saved-session-pzl-toggle-action">
          <span className="pzl-source-badge">Niezależny cennik sesji</span>
          <span>{isExpanded ? 'Zwiń' : 'Rozwiń'}</span>
          <span className={`billing-section-chevron${isExpanded ? ' is-expanded' : ''}`} aria-hidden="true">
            <ChevronDown size={18} />
          </span>
        </span>
      </button>
      {isExpanded && (
      <div id={contentId} className="saved-session-pzl-content">
      <p className="saved-session-pzl-description">
        Zmiany dotyczą wyłącznie tej sesji i PZL korzystających z jej cennika.
      </p>
      {error && <div className="period-usl-error">{error}</div>}
      {message && <div className="pzl-price-list-success">{message}</div>}
      <form className="pzl-price-list-form is-session" onSubmit={submit}>
        <div className="pzl-price-list-step-heading is-compact">
          <span>1</span>
          <div>
            <h4>Rozliczanie PZL</h4>
            <p>Wybierz sposób rozliczenia obowiązujący w tej zapisanej sesji.</p>
          </div>
        </div>
        <div className="pzl-price-list-options">
          {[
            ['per_person', 'Kwota / osoba'],
            ['usl', 'SUMA USL'],
            ['equal_split', 'Pula po równo'],
          ].map(([mode, label]) => (
            <label className={`pzl-price-option${pricing.settlement_mode === mode ? ' is-selected' : ''}`} key={mode}>
              <input
                type="radio"
                name={`saved-pzl-price-mode-${settlement.id}`}
                value={mode}
                checked={pricing.settlement_mode === mode}
                onChange={() => setPricing((current) => ({ ...current, settlement_mode: mode }))}
              />
              <strong>{label}</strong>
            </label>
          ))}
        </div>
        <div className="pzl-price-list-actions">
          <label className="pzl-price-amount-label">
            <span>
              {pricing.settlement_mode === 'equal_split'
                ? 'Łączna pula'
                : pricing.settlement_mode === 'usl' ? 'Suma USL' : 'Cena'}
            </span>
            <span className="pzl-money-input">
              <input
                type="text"
                inputMode="decimal"
                value={pricing.amount}
                onChange={(event) => setPricing((current) => ({ ...current, amount: event.target.value }))}
                disabled={saving}
              />
              <span>PLN</span>
            </span>
          </label>
          <span className="pzl-price-current-value">
            {pricing.settlement_mode === 'usl'
              ? `${formatMoney(pricing.amount)} zostanie rozliczone według zapisanych wag.`
              : `${formatMoney(pricing.amount)}${pricing.settlement_mode === 'per_person' ? ' dla każdej osoby' : ' do podziału'}`}
          </span>
          <button type="submit" className="btn-primary btn-sm" disabled={saving}>
            <Save size={14} aria-hidden="true" /> {saving ? 'Przeliczanie…' : 'Zapisz cennik sesji'}
          </button>
        </div>
      </form>
      </div>
      )}
    </section>
  );
}

function UnassignedInstallationsWarning({ installations, onNavigateToPlanning }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentId = 'unassigned-installations-content';
  const toggleSection = () => setIsExpanded((current) => !current);

  return (
    <div className={`unassigned-warning${isExpanded ? ' is-expanded' : ' is-collapsed'}`}>
      <div
        className="unassigned-warning-header"
        onClick={toggleSection}
        onKeyDown={(event) => {
          if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            toggleSection();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls={contentId}
      >
        <span className="unassigned-warning-badge">{installations.length}</span>
        <div className="unassigned-warning-copy">
          <strong>Zaplanowane montaże bez przypisanego montażysty</strong>
          <span className="unassigned-warning-hint">
            Poniższe zlecenia mają datę montażu, ale nie przypisano do nich montażysty.
            {onNavigateToPlanning ? (
              <>
                {' '}Przejdź do zakładki{' '}
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onNavigateToPlanning();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3b82f6',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    padding: 0,
                    font: 'inherit',
                  }}
                >
                  Planowanie
                </button>
                , otwórz zlecenie i przypisz montażystę.
              </>
            ) : (
              ' Przejdź do zakładki Planowanie, otwórz zlecenie i przypisz montażystę.'
            )}
          </span>
        </div>
        <span className="unassigned-warning-toggle" aria-hidden="true">
          {isExpanded ? 'Zwiń' : 'Rozwiń'}
          <ChevronDown size={17} aria-hidden="true" />
        </span>
      </div>
      {isExpanded && (
        <div className="unassigned-warning-content" id={contentId}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Zlecenie</th>
                <th>Kraj</th>
                <th>Kontrahent</th>
                <th>Data montażu</th>
                <th>Status</th>
                <th>Wartość USL</th>
              </tr>
            </thead>
            <tbody>
              {[...installations].sort(compareInstallationsByDate).map((inst) => (
                <tr key={inst.installation_id}>
                  <td className="order-cell">{inst.order_number}</td>
                  <td>{inst.country || '—'}</td>
                  <td>{inst.contractor || '—'}</td>
                  <td>{formatInstallationDate(inst)}</td>
                  <td>{installationStatusLabel(inst.calendar_status)}</td>
                  <td className="numeric-cell">{inst.total_usl_value.toFixed(2)} PLN</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PzlSettlementEditor({ item, onSave, isSnapshot = false }) {
  const [usePriceList, setUsePriceList] = useState(item.pzl_uses_price_list !== false);
  const [mode, setMode] = useState(item.pzl_settlement_mode || 'usl');
  const [amount, setAmount] = useState(String(item.pzl_settlement_amount ?? 0));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setUsePriceList(item.pzl_uses_price_list !== false);
    setMode(item.pzl_settlement_mode || 'usl');
    setAmount(String(item.pzl_settlement_amount ?? 0));
  }, [
    item.pzl_uses_price_list,
    item.pzl_settlement_mode,
    item.pzl_settlement_amount,
  ]);

  async function savePricing(saveGlobally = false) {
    const parsedAmount = Number(String(amount).replace(',', '.'));
    if (!usePriceList && (!Number.isFinite(parsedAmount) || parsedAmount <= 0)) {
      setError('Wprowadź kwotę większą od 0.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        use_price_list: usePriceList,
        settlement_mode: usePriceList ? undefined : mode,
        amount: usePriceList ? undefined : (Number.isFinite(parsedAmount) ? parsedAmount : 0),
      };
      if (isSnapshot) payload.save_globally = saveGlobally;
      await onSave(item.installation_id, payload);
      setSuccess(
        saveGlobally
          ? 'Zapisano w tej sesji oraz jako indywidualne ustawienie PZL w systemie.'
          : isSnapshot ? 'Zapisano tylko w tej sesji rozliczeniowej.' : 'Ustawienie PZL zostało zapisane.'
      );
    } catch (_) {
      setError('Nie udało się zapisać ustawienia PZL.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      className="pzl-order-editor"
      onSubmit={(event) => {
        event.preventDefault();
        savePricing(false);
      }}
    >
      <div className="pzl-order-editor-heading">
        <div>
          <strong>Rozliczanie PZL</strong>
          <span>
            Aktualnie: {pzlSettlementModeLabel(item.pzl_settlement_mode)}
            {` · ${formatMoney(item.pzl_settlement_amount)}`}
          </span>
        </div>
        <span className={`pzl-source-badge${item.pzl_uses_price_list ? '' : ' is-override'}`}>
          {item.pzl_uses_price_list ? 'Z cennika' : 'Ustawienie indywidualne'}
        </span>
      </div>
      <label className="pzl-use-price-list">
        <input
          type="checkbox"
          checked={usePriceList}
          onChange={(event) => setUsePriceList(event.target.checked)}
          disabled={saving}
        />
        Pobieraj ustawienie z cennika
      </label>
      {!usePriceList && (
        <div className="pzl-order-editor-controls">
          <label>
            Sposób rozliczenia
            <select value={mode} onChange={(event) => setMode(event.target.value)} disabled={saving}>
              <option value="per_person">Kwota dla każdej osoby</option>
              <option value="usl">SUMA USL – według wag</option>
              <option value="equal_split">Kwota łączna po równo</option>
            </select>
          </label>
          <label>
            {mode === 'usl' ? 'Suma USL' : 'Kwota'}
            <span className="pzl-money-input">
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                disabled={saving}
              />
              <span>PLN</span>
            </span>
          </label>
        </div>
      )}
      {isSnapshot && !usePriceList && (
        <p className="pzl-order-global-note">
          Zapis w systemie utworzy indywidualne ustawienie tego zlecenia.
          Późniejsze zmiany cennika domyślnego nie będą go nadpisywać.
        </p>
      )}
      <div className="pzl-order-editor-actions">
        {error && <span className="installation-usl-error">{error}</span>}
        {success && <span className="pzl-order-save-success">{success}</span>}
        <div className={`pzl-order-save-buttons${isSnapshot && !usePriceList ? ' has-two-actions' : ''}`}>
          <button type="submit" className="btn-primary btn-sm" disabled={saving}>
            <Save size={14} aria-hidden="true" />
            {saving ? 'Zapisywanie…' : isSnapshot ? 'Zapisz tylko w tej sesji' : 'Zapisz sposób PZL'}
          </button>
          {isSnapshot && !usePriceList && (
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={saving}
              onClick={() => savePricing(true)}
            >
              <Save size={14} aria-hidden="true" /> Zapisz również w systemie
            </button>
          )}
        </div>
      </div>
    </form>
  );
}

function NoteSettlementEditor({ installation, onSave }) {
  const assignedInstallers = installation.assigned_installers || [];
  const initialMode = ['equal', 'manual'].includes(installation.settlement_mode)
    ? installation.settlement_mode
    : 'weights';
  const [mode, setMode] = useState(initialMode);
  const [overhead, setOverhead] = useState(String(installation.overhead_percentage ?? 5));
  const [distribution, setDistribution] = useState(String(installation.distribution_percentage ?? 25));
  const [equalAmount, setEqualAmount] = useState(String(assignedInstallers[0]?.amount ?? 0));
  const [manualAmounts, setManualAmounts] = useState(() => Object.fromEntries(
    assignedInstallers.map((installer) => [installer.installer_id, String(installer.amount ?? 0)])
  ));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setMode(['equal', 'manual'].includes(installation.settlement_mode)
      ? installation.settlement_mode
      : 'weights');
    setOverhead(String(installation.overhead_percentage ?? 5));
    setDistribution(String(installation.distribution_percentage ?? 25));
    setEqualAmount(String((installation.assigned_installers || [])[0]?.amount ?? 0));
    setManualAmounts(Object.fromEntries(
      (installation.assigned_installers || []).map((installer) => [
        installer.installer_id,
        String(installer.amount ?? 0),
      ])
    ));
  }, [installation]);

  function parseAmount(value) {
    return Number(String(value).replace(',', '.'));
  }

  async function save(saveGlobally) {
    const parsedOverhead = parseAmount(overhead);
    const parsedDistribution = parseAmount(distribution);
    if (!Number.isFinite(parsedOverhead) || parsedOverhead < 0 || parsedOverhead > 100) {
      setError('Narzut musi mieścić się w zakresie od 0 do 100%.');
      return;
    }
    if (!Number.isFinite(parsedDistribution) || parsedDistribution < 0 || parsedDistribution > 100) {
      setError('Część do podziału musi mieścić się w zakresie od 0 do 100%.');
      return;
    }

    const payload = {
      overhead_percentage: parsedOverhead,
      distribution_percentage: parsedDistribution,
      settlement_mode: mode,
      save_globally: saveGlobally,
    };
    if (mode === 'equal') {
      const parsedEqualAmount = parseAmount(equalAmount);
      if (!Number.isFinite(parsedEqualAmount) || parsedEqualAmount < 0) {
        setError('Jednakowa kwota nie może być ujemna.');
        return;
      }
      payload.equal_amount = parsedEqualAmount;
    }
    if (mode === 'manual') {
      const parsedManualAmounts = Object.fromEntries(
        assignedInstallers.map((installer) => [
          installer.installer_id,
          parseAmount(manualAmounts[installer.installer_id] ?? 0),
        ])
      );
      if (Object.values(parsedManualAmounts).some(
        (amount) => !Number.isFinite(amount) || amount < 0
      )) {
        setError('Niestandardowe kwoty nie mogą być ujemne.');
        return;
      }
      payload.manual_amounts = parsedManualAmounts;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await onSave(installation.installation_id, payload);
      setSuccess(saveGlobally
        ? 'Zapisano w tej sesji oraz jako ustawienie zlecenia w systemie.'
        : 'Zapisano tylko w tej sesji rozliczeniowej.');
    } catch (requestError) {
      setError(apiError(requestError, 'Nie udało się zapisać sposobu rozliczenia notatki.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="settlement-calculation-editor note-settlement-editor">
      <div className="settlement-calculation-editor-heading">
        <div className="settlement-calculation-editor-title">
          <span className="settlement-calculation-editor-icon" aria-hidden="true">
            <SlidersHorizontal size={18} />
          </span>
          <div>
            <span className="calculation-eyebrow">Rozliczenie notatki</span>
            <h5>Sposób podziału kwoty</h5>
            <p>Ustaw końcowe kwoty dla wszystkich osób przypisanych do tej notatki.</p>
          </div>
        </div>
      </div>

      <div className="settlement-calculation-editor-body">
        <div className="settlement-calculation-group">
          <div className="settlement-calculation-group-heading">
            <span>1</span>
            <div>
              <strong>Sposób rozliczenia</strong>
              <small>Dotyczy wszystkich przypisanych pracowników</small>
            </div>
          </div>
          <div className="note-settlement-mode-options">
            <label>
              <input type="radio" name={`note-mode-${installation.installation_id}`} value="weights"
                checked={mode === 'weights'} onChange={() => setMode('weights')} disabled={saving} />
              Kwota USL po narzucie i procencie, według wag
            </label>
            <label>
              <input type="radio" name={`note-mode-${installation.installation_id}`} value="equal"
                checked={mode === 'equal'} onChange={() => setMode('equal')} disabled={saving} />
              Każda osoba otrzymuje tę samą kwotę
            </label>
            <label>
              <input type="radio" name={`note-mode-${installation.installation_id}`} value="manual"
                checked={mode === 'manual'} onChange={() => setMode('manual')} disabled={saving} />
              Niestandardowe kwoty dla poszczególnych osób
            </label>
          </div>
        </div>

        {mode === 'weights' && (
          <div className="settlement-percentage-fields">
            <label>
              <span>Narzut</span>
              <span className="settlement-percentage-input">
                <input type="text" inputMode="decimal" value={overhead}
                  onChange={(event) => setOverhead(event.target.value)} disabled={saving} />
                <span>%</span>
              </span>
            </label>
            <label>
              <span>Część do podziału</span>
              <span className="settlement-percentage-input">
                <input type="text" inputMode="decimal" value={distribution}
                  onChange={(event) => setDistribution(event.target.value)} disabled={saving} />
                <span>%</span>
              </span>
            </label>
          </div>
        )}

        {mode === 'equal' && (
          <label className="note-settlement-equal-amount">
            <span>Kwota dla każdej osoby</span>
            <span className="pzl-money-input">
              <input type="text" inputMode="decimal" value={equalAmount}
                onChange={(event) => setEqualAmount(event.target.value)} disabled={saving} />
              <span>PLN</span>
            </span>
          </label>
        )}

        {mode === 'manual' && (
          <div className="note-settlement-manual-amounts">
            {assignedInstallers.map((installer) => (
              <label key={installer.installer_id}>
                <span>{installer.first_name} {installer.last_name}</span>
                <span className="pzl-money-input">
                  <input type="text" inputMode="decimal"
                    value={manualAmounts[installer.installer_id] ?? ''}
                    onChange={(event) => setManualAmounts((current) => ({
                      ...current,
                      [installer.installer_id]: event.target.value,
                    }))}
                    disabled={saving} />
                  <span>PLN</span>
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {(error || success) && (
        <div className={`settlement-calculation-feedback${error ? ' is-error' : ' is-success'}`}>
          {error ? <X size={16} aria-hidden="true" /> : <Check size={16} aria-hidden="true" />}
          <span>{error || success}</span>
        </div>
      )}

      <div className="settlement-calculation-editor-footer">
        <div className="settlement-calculation-global-note">
          <Info size={16} aria-hidden="true" />
          <span>Zapis systemowy zastosuje te ustawienia również w kolejnych rozliczeniach.</span>
        </div>
        <div className="settlement-calculation-save-buttons">
          <button type="button" className="btn-primary btn-sm" disabled={saving || assignedInstallers.length === 0}
            onClick={() => save(false)}>
            <Save size={15} aria-hidden="true" /> {saving ? 'Zapisywanie…' : 'Zapisz tylko w tej sesji'}
          </button>
          <button type="button" className="btn-secondary btn-sm" disabled={saving || assignedInstallers.length === 0}
            onClick={() => save(true)}>
            <Save size={15} aria-hidden="true" /> Zapisz również w systemie
          </button>
        </div>
      </div>
    </section>
  );
}

function PeriodInstallationsTable({
  installations,
  loading,
  onAddUsl,
  onUpdateUsl,
  onUpdatePzlPricing,
  onUpdateCalculation,
  isSnapshot,
}) {
  const [expandedInstallationId, setExpandedInstallationId] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const contentId = 'period-installations-content';

  function toggleSection() {
    if (isExpanded) setExpandedInstallationId(null);
    setIsExpanded((current) => !current);
  }

  return (
    <section className={`period-installations-card${isExpanded ? ' is-expanded' : ' is-collapsed'}`}>
      <button
        type="button"
        className="period-installations-header"
        onClick={toggleSection}
        aria-expanded={isExpanded}
        aria-controls={contentId}
      >
        <div>
          <h4>Wszystkie montaże w tym okresie</h4>
          <p>
            Lista obejmuje przypisane zlecenia, których termin nachodzi na wybrany zakres dat.
          </p>
        </div>
        <div className="period-installations-header-actions">
          <span className="period-installations-count">
            {loading ? '…' : installations.length}
          </span>
          <span className="period-installations-section-toggle" aria-hidden="true">
            {isExpanded ? 'Zwiń' : 'Rozwiń'}
            <ChevronDown size={17} aria-hidden="true" />
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className="period-installations-content" id={contentId}>
          {loading ? (
            <div className="period-installations-state">Pobieranie montaży…</div>
          ) : installations.length === 0 ? (
            <div className="period-installations-state">
              Brak przypisanych montaży w wybranym okresie.
            </div>
          ) : (
            <div className="period-installations-table-wrap">
              <table className="data-table period-installations-table">
            <thead>
              <tr>
                <th>Zlecenie</th>
                <th>Kraj</th>
                <th>Kontrahent</th>
                <th>Data montażu</th>
                <th>Status</th>
                <th>Suma USL</th>
                <th>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {[...installations].sort(compareInstallationsByDate).map((installation) => {
                const isExpanded = expandedInstallationId === installation.installation_id;
                const hasNoUsl = Number(installation.total_usl_value || 0) <= 0;
                const toggleInstallation = () => setExpandedInstallationId(
                  isExpanded ? null : installation.installation_id
                );
                return (
                  <Fragment key={installation.installation_id}>
                    <tr
                      className={`period-installation-row${hasNoUsl ? ' has-zero-usl' : ''}${isExpanded ? ' is-expanded' : ''}`}
                      onClick={toggleInstallation}
                      onKeyDown={(e) => {
                        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          toggleInstallation();
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isExpanded}
                    >
                      <td className="order-cell">
                        {installation.order_number}
                        {isPzlItem(installation) && !isSnapshot && (
                          <span className="pzl-inline-badge">
                            {pzlSettlementModeLabel(installation.pzl_settlement_mode)}
                          </span>
                        )}
                      </td>
                      <td>{installation.country || '—'}</td>
                      <td>{installation.contractor || '—'}</td>
                      <td>
                        {formatPolishDate(installation.planned_start_date)}
                        {installation.planned_end_date
                          && installation.planned_end_date !== installation.planned_start_date
                          ? ` – ${formatPolishDate(installation.planned_end_date)}`
                          : ''}
                      </td>
                      <td>{installationStatusLabel(installation.calendar_status)}</td>
                      <td className="numeric-cell">
                        <span className={hasNoUsl ? 'period-installation-usl is-zero' : 'period-installation-usl'}>
                          {formatMoney(installation.total_usl_value)}
                        </span>
                      </td>
                      <td className="period-installation-actions">
                        <button
                          type="button"
                          className={`btn-secondary btn-sm period-installation-toggle${isExpanded ? ' is-expanded' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleInstallation();
                          }}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? 'Zwiń' : 'Rozwiń'}
                          <ChevronDown size={14} aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="period-installation-editor-row">
                        <td colSpan={7}>
                          <PeriodInstallationUslEditor
                            installation={installation}
                            onAddUsl={onAddUsl}
                            onUpdateUsl={onUpdateUsl}
                            onUpdatePzlPricing={onUpdatePzlPricing}
                            onUpdateCalculation={onUpdateCalculation}
                            isSnapshot={isSnapshot}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function PeriodInstallationUslEditor({
  installation,
  onAddUsl,
  onUpdateUsl,
  onUpdatePzlPricing,
  onUpdateCalculation,
  isSnapshot,
}) {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [editingItemId, setEditingItemId] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function startEditing(entry) {
    setEditingItemId(entry.item_id);
    setEditAmount(String(entry.amount));
    setEditDescription(entry.description || '');
    setError('');
  }

  function cancelEditing() {
    setEditingItemId(null);
    setEditAmount('');
    setEditDescription('');
    setError('');
  }

  async function submitNewUsl(e) {
    e.preventDefault();
    const parsedAmount = Number(String(amount).replace(',', '.'));
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setError('Wprowadź kwotę większą od 0.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onAddUsl(
        installation.installation_id,
        parsedAmount,
        description.trim() || undefined
      );
      setAmount('');
      setDescription('');
    } catch (e) {
      setError(apiError(e, 'Nie udało się dodać USL.'));
    } finally {
      setSaving(false);
    }
  }

  async function submitEditedUsl(e, itemId) {
    e.preventDefault();
    const parsedAmount = Number(String(editAmount).replace(',', '.'));
    if (!Number.isFinite(parsedAmount) || parsedAmount < 0) {
      setError('Wprowadź kwotę równą lub większą od 0.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onUpdateUsl(
        installation.installation_id,
        itemId,
        parsedAmount,
        editDescription.trim() || undefined
      );
      cancelEditing();
    } catch (e) {
      setError(apiError(e, 'Nie udało się zaktualizować USL.'));
    } finally {
      setSaving(false);
    }
  }

  const entries = installation.editable_usl_entries || [];
  const assignedInstallers = installation.assigned_installers || [];
  const hideUslControls = isSnapshot && isPzlItem(installation);

  return (
    <div className="period-usl-editor">
      {isPzlItem(installation) && (
        <PzlSettlementEditor
          item={installation}
          onSave={onUpdatePzlPricing}
          isSnapshot={isSnapshot}
        />
      )}
      {isSnapshot && installation.calendar_status === 'calendar_note' && (
        <NoteSettlementEditor
          installation={installation}
          onSave={onUpdateCalculation}
        />
      )}
      <div className="period-installation-breakdown">
        <div className="period-installation-breakdown-heading">
          <div>
            <strong>Rozliczenie przypisanych pracowników</strong>
            <span>Kwoty dotyczą dni przypisania mieszczących się w wybranym okresie.</span>
          </div>
          <span>{assignedInstallers.length} os.</span>
        </div>
        <AssignedInstallersBreakdownContent assignedInstallers={assignedInstallers} />
      </div>

      {!hideUslControls && (
        <>
          <div className="period-usl-editor-heading">
            <div>
              <strong>USL zlecenia {installation.order_number}</strong>
              <span>Łączna wartość: {formatMoney(installation.total_usl_value)}</span>
            </div>
            {entries.length === 0 && (
              <span className="period-usl-no-entries">
                Brak pozycji USL dodanych w module Rozliczenia.
              </span>
            )}
          </div>

          {entries.length > 0 && (
            <div className="period-usl-entries">
              {entries.map((entry) => (
                <div className="period-usl-entry" key={entry.item_id}>
                  {editingItemId === entry.item_id ? (
                    <form onSubmit={(e) => submitEditedUsl(e, entry.item_id)} className="period-usl-inline-form">
                      <label>
                        Kwota USL
                        <input
                          type="text"
                          inputMode="decimal"
                          value={editAmount}
                          onChange={(e) => setEditAmount(e.target.value)}
                          disabled={saving}
                        />
                      </label>
                      <label>
                        Opis
                        <input
                          type="text"
                          maxLength={200}
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          disabled={saving}
                        />
                      </label>
                      <div className="period-usl-form-actions">
                        <button type="submit" className="btn-primary btn-sm" disabled={saving}>
                          <Check size={14} aria-hidden="true" /> Zapisz
                        </button>
                        <button type="button" className="btn-secondary btn-sm" onClick={cancelEditing} disabled={saving}>
                          <X size={14} aria-hidden="true" /> Anuluj
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div>
                        <strong>{entry.description || 'USL wprowadzone w rozliczeniu'}</strong>
                        <span>{formatMoney(entry.amount)}</span>
                      </div>
                      <button type="button" className="btn-secondary btn-sm" onClick={() => startEditing(entry)} disabled={saving}>
                        <Pencil size={14} aria-hidden="true" /> Edytuj
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          <form className="period-usl-add-form" onSubmit={submitNewUsl}>
            <label>
              Nowa kwota USL
              <input
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={saving}
              />
            </label>
            <label>
              Opis (opcjonalnie)
              <input
                type="text"
                maxLength={200}
                placeholder="np. prace dodatkowe"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={saving}
              />
            </label>
            <button type="submit" className="btn-primary" disabled={saving}>
              <Plus size={15} aria-hidden="true" />
              {saving ? 'Zapisywanie…' : 'Dodaj USL'}
            </button>
          </form>
          {error && <div className="period-usl-error">{error}</div>}
        </>
      )}
    </div>
  );
}

function AssignedInstallersBreakdownContent({ assignedInstallers }) {
  if (assignedInstallers.length === 0) {
    return (
      <div className="period-installation-breakdown-empty">
        Brak danych rozliczeniowych przypisanych pracowników.
      </div>
    );
  }

  return (
    <div className="period-installation-breakdown-table-wrap">
      <table className="period-installation-breakdown-table">
        <thead>
          <tr>
            <th>Imię</th>
            <th>Nazwisko</th>
            <th>Stanowisko</th>
            <th className="numeric-cell">Waga</th>
            <th className="numeric-cell">Dni</th>
            <th className="numeric-cell">Udział</th>
            <th className="numeric-cell">Kwota</th>
          </tr>
        </thead>
        <tbody>
          {assignedInstallers.map((assignedInstaller) => (
            <tr key={assignedInstaller.installer_id}>
              <td>{assignedInstaller.first_name}</td>
              <td><strong>{assignedInstaller.last_name}</strong></td>
              <td>{assignedInstaller.position || '—'}</td>
              <td className="numeric-cell">{formatNumber(assignedInstaller.weight)}</td>
              <td className="numeric-cell">{assignedInstaller.day_count || '—'}</td>
              <td className="numeric-cell">{formatNumber(assignedInstaller.percentage)}%</td>
              <td className="numeric-cell"><strong>{formatMoney(assignedInstaller.amount)}</strong></td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={6}>Razem</td>
            <td className="numeric-cell">
              {formatMoney(assignedInstallers.reduce(
                (sum, assignedInstaller) => sum + Number(assignedInstaller.amount || 0),
                0
              ))}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function CollapsibleAssignedInstallersBreakdown({ assignedInstallers, contentId }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section className={`period-installation-breakdown is-collapsible${isExpanded ? ' is-expanded' : ' is-collapsed'}`}>
      <button
        type="button"
        className="period-installation-breakdown-heading"
        onClick={() => setIsExpanded((current) => !current)}
        aria-expanded={isExpanded}
        aria-controls={contentId}
      >
        <div>
          <strong>Rozliczenie przypisanych pracowników</strong>
          <span>Kwoty dotyczą dni przypisania mieszczących się w wybranym okresie.</span>
        </div>
        <div className="period-installation-breakdown-heading-actions">
          <span>{assignedInstallers.length} os.</span>
          <span className="period-installation-breakdown-toggle">
            {isExpanded ? 'Zwiń' : 'Rozwiń'}
            <ChevronDown size={15} aria-hidden="true" />
          </span>
        </div>
      </button>
      {isExpanded && (
        <div id={contentId}>
          <AssignedInstallersBreakdownContent assignedInstallers={assignedInstallers} />
        </div>
      )}
    </section>
  );
}

function settlementModeLabel(mode) {
  if (mode === 'manual') return 'Kwoty wprowadzone ręcznie';
  if (mode === 'weights') return 'Podział według wag stanowisk';
  return 'Brak danych w starszym raporcie';
}

function InstallerEarningsCard({
  installer,
  pdfLoading,
  onPdfExport,
  expandedRow,
  onToggleRow,
  onAddUsl,
  onUpdatePzlPricing,
  onUpdateCalculation,
  onUpdateTeamAdditionalAmount,
  isSnapshot,
  periodInstallations,
}) {
  const [isTableExpanded, setIsTableExpanded] = useState(false);
  const toggleTable = () => setIsTableExpanded((current) => !current);

  return (
    <div className={`installer-earnings-card${isSnapshot && !isTableExpanded ? ' is-collapsed' : ''}`}>
      <div
        className={`installer-earnings-header${isSnapshot ? ' is-toggleable' : ''}`}
        onClick={isSnapshot ? toggleTable : undefined}
        onKeyDown={isSnapshot ? (event) => {
          if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            toggleTable();
          }
        } : undefined}
        role={isSnapshot ? 'button' : undefined}
        tabIndex={isSnapshot ? 0 : undefined}
        aria-expanded={isSnapshot ? isTableExpanded : undefined}
      >
        <div>
          <h4>{installer.full_name}</h4>
          <span className="installer-position">
            {installer.position}
          </span>
        </div>
        <div className="installer-earnings-total">
          <span className="total-earned">{installer.total_earned.toFixed(2)} PLN</span>
          <button
            type="button"
            className="btn-primary btn-sm"
            onClick={(event) => {
              event.stopPropagation();
              onPdfExport(installer.installer_id);
            }}
            disabled={pdfLoading === installer.installer_id}
          >
            {pdfLoading === installer.installer_id ? '…' : '📄 PDF'}
          </button>
          {isSnapshot && (
            <span className="period-installations-section-toggle installer-earnings-section-toggle" aria-hidden="true">
              {isTableExpanded ? 'Zwiń' : 'Rozwiń'}
              <ChevronDown size={15} aria-hidden="true" />
            </span>
          )}
        </div>
      </div>

      {(!isSnapshot || isTableExpanded) && (
      <table className="data-table earnings-table">
        <thead>
          <tr>
            <th>Data</th>
            <th className="earnings-days-cell">Dni</th>
            <th>Zlecenie / pozycja</th>
            <th>Kraj</th>
            <th>Kontrahent / typ</th>
            <th>Wartość USL / stawka</th>
            <th>Udział</th>
            <th>Zarobek</th>
          </tr>
        </thead>
        <tbody>
          {[...installer.installations].sort(compareInstallationsByDate).map((item) => {
            const rowKey = `${installer.installer_id}:${item.installation_id}`;
            const periodInstallation = periodInstallations.find(
              (installation) => installation.installation_id === item.installation_id
            );
            return (
              <InstallationEarningsRows
                key={rowKey}
                item={item}
                installer={installer}
                expanded={expandedRow === rowKey}
                onToggle={() => onToggleRow(rowKey)}
                onAddUsl={onAddUsl}
                onUpdatePzlPricing={onUpdatePzlPricing}
                onUpdateCalculation={onUpdateCalculation}
                onUpdateTeamAdditionalAmount={onUpdateTeamAdditionalAmount}
                isSnapshot={isSnapshot}
                assignedInstallers={periodInstallation?.assigned_installers || []}
                installationItems={periodInstallation?.items || []}
              />
            );
          })}
          {(installer.vacations || []).map((vacation) => (
            <tr
              className="vacation-earning-row"
              key={`vacation:${installer.installer_id}:${vacation.vacation_id}`}
            >
              <td>{formatVacationDates(vacation)}</td>
              <td className="numeric-cell earnings-days-cell">{vacation.day_count}</td>
              <td><strong>Urlop {vacation.vacation_type}</strong></td>
              <td>—</td>
              <td>{VACATION_TYPE_LABELS[vacation.vacation_type] || vacation.vacation_type}</td>
              <td className="numeric-cell">{formatMoney(vacation.daily_rate)} / dzień</td>
              <td className="numeric-cell">—</td>
              <td className="numeric-cell"><strong>{formatMoney(vacation.earned)}</strong></td>
            </tr>
          ))}
          <tr className="total-row">
            <td colSpan={7}><strong>Razem</strong></td>
            <td className="numeric-cell"><strong>{installer.total_earned.toFixed(2)} PLN</strong></td>
          </tr>
        </tbody>
      </table>
      )}
    </div>
  );
}

function InstallationEarningsRows({
  item,
  installer,
  expanded,
  onToggle,
  onAddUsl,
  onUpdatePzlPricing,
  onUpdateCalculation,
  onUpdateTeamAdditionalAmount,
  isSnapshot,
  assignedInstallers,
  installationItems,
}) {
  const [uslAmount, setUslAmount] = useState('');
  const [uslDescription, setUslDescription] = useState('');
  const [savingUsl, setSavingUsl] = useState(false);
  const [inputError, setInputError] = useState('');

  async function submitUsl(e) {
    e.preventDefault();
    const amount = Number(String(uslAmount).replace(',', '.'));
    if (!Number.isFinite(amount) || amount <= 0) {
      setInputError('Wprowadź kwotę większą od 0');
      return;
    }

    setSavingUsl(true);
    setInputError('');
    try {
      await onAddUsl(item.installation_id, amount, uslDescription.trim() || undefined);
      setUslAmount('');
      setUslDescription('');
    } catch (e) {
      setInputError(apiError(e, 'Nie udało się dodać USL'));
    } finally {
      setSavingUsl(false);
    }
  }

  function handleRowKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  }

  return (
    <>
      <tr
        className={`earnings-installation-row${expanded ? ' is-expanded' : ''}${expanded && isSnapshot ? ' is-session-expanded' : ''}`}
        onClick={onToggle}
        onKeyDown={handleRowKeyDown}
        tabIndex={0}
        aria-expanded={expanded}
      >
        <td>{formatInstallationDate(item)}</td>
        <td className="numeric-cell earnings-days-cell">{installationAssignedDayCount(item)}</td>
        <td>
          <span className="earnings-order-number">
            {item.order_number}
            <ChevronDown size={15} aria-hidden="true" />
          </span>
          {isPzlItem(item) && !isSnapshot && (
            <span className="pzl-inline-badge">
              {pzlSettlementModeLabel(item.pzl_settlement_mode)}
            </span>
          )}
        </td>
        <td>{item.country || '—'}</td>
        <td>{item.contractor || '—'}</td>
        <td className="numeric-cell">
          {isPzlItem(item) && item.pzl_settlement_mode && item.pzl_settlement_mode !== 'usl'
            ? `${formatMoney(item.pzl_settlement_amount)} ${item.pzl_settlement_mode === 'per_person' ? '/ os.' : 'pula'}`
            : `${item.total_usl_value.toFixed(2)} PLN`}
        </td>
        <td className="numeric-cell">{item.percentage}%</td>
        <td className="numeric-cell earned-cell">{item.earned.toFixed(2)} PLN</td>
      </tr>
      {expanded && (
        <tr className={`earnings-installation-details-row${isSnapshot ? ' is-session-details' : ''}`}>
          <td colSpan={8}>
            <div className="earnings-installation-details">
              <div className="installation-detail-summary">
                <div>
                  <span>Suma USL</span>
                  <strong>{item.total_usl_value.toFixed(2)} PLN</strong>
                </div>
                <div>
                  <span>Sposób rozliczenia</span>
                  <strong>{settlementModeLabel(item.settlement_mode)}</strong>
                </div>
                <div>
                  <span>Parametry podziału</span>
                  <strong>
                    {item.overhead_percentage == null || item.distribution_percentage == null
                      ? 'Brak danych w starszym raporcie'
                      : `Narzut ${item.overhead_percentage}% · Podział ${item.distribution_percentage}%`}
                  </strong>
                </div>
              </div>

              {isPzlItem(item) && (
                <PzlSettlementEditor
                  item={item}
                  onSave={onUpdatePzlPricing}
                  isSnapshot={isSnapshot}
                />
              )}

              {isSnapshot && !(
                isPzlItem(item)
                && item.pzl_settlement_mode
                && item.pzl_settlement_mode !== 'usl'
              ) && (
                <SettlementCalculationEditor
                  item={item}
                  installer={installer}
                  onSave={onUpdateCalculation}
                />
              )}

              <InstallationCalculation
                item={item}
                installer={installer}
                isSnapshot={isSnapshot}
                onUpdateTeamAdditionalAmount={onUpdateTeamAdditionalAmount}
              />

              <CollapsibleAssignedInstallersBreakdown
                assignedInstallers={assignedInstallers}
                contentId={`assigned-installers-${installer.installer_id}-${item.installation_id}`}
              />

              {!(isSnapshot && isPzlItem(item)) && (
                <form className="installation-usl-form" onSubmit={submitUsl}>
                  <div className="installation-usl-form-heading">
                    <strong>Dodaj pozycję USL</strong>
                    <span>
                      {isSnapshot
                        ? 'Zapisane rozliczenie zostanie przeliczone z zachowaniem zapisanych wag.'
                        : 'Kwota zostanie dodana do obecnej sumy USL zlecenia.'}
                    </span>
                  </div>
                  <label>
                    Kwota USL
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="0,00"
                      value={uslAmount}
                      onChange={(e) => setUslAmount(e.target.value)}
                      disabled={savingUsl}
                    />
                  </label>
                  <label>
                    Opis (opcjonalnie)
                    <input
                      type="text"
                      maxLength={200}
                      placeholder="np. Prace dodatkowe"
                      value={uslDescription}
                      onChange={(e) => setUslDescription(e.target.value)}
                      disabled={savingUsl}
                    />
                  </label>
                  <button type="submit" className="btn-primary" disabled={savingUsl}>
                    <Plus size={15} aria-hidden="true" />
                    {savingUsl ? 'Dodawanie…' : 'Dodaj USL'}
                  </button>
                  {inputError && <span className="installation-usl-error">{inputError}</span>}
                </form>
              )}

              {isSnapshot && (
                <InstallationItemsDetails
                  items={installationItems}
                  contentId={`installation-items-${installer.installer_id}-${item.installation_id}`}
                />
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function InstallationItemsDetails({ items, contentId }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const sortedItems = [...items].sort((left, right) => (
    String(left.product_type || '').localeCompare(
      String(right.product_type || ''),
      'pl',
      { sensitivity: 'base' }
    )
  ));
  const totalUsl = items.reduce((sum, detailItem) => (
    (detailItem.product_type || '').trim().toUpperCase() === 'USL'
      ? sum + Number(detailItem.net_price_pln || 0)
      : sum
  ), 0);

  return (
    <section className={`saved-installation-items-details${isExpanded ? ' is-expanded' : ''}`}>
      <button
        type="button"
        className="saved-installation-items-toggle"
        onClick={() => setIsExpanded((current) => !current)}
        aria-expanded={isExpanded}
        aria-controls={contentId}
      >
        <span>{isExpanded ? 'Ukryj szczegóły' : 'Pokaż szczegóły'}</span>
        <ChevronDown size={16} aria-hidden="true" />
      </button>
      {isExpanded && (
        <div className="saved-installation-items-content" id={contentId}>
          {sortedItems.length === 0 ? (
            <div className="period-installation-breakdown-empty">
              Brak pozycji zlecenia do wyświetlenia.
            </div>
          ) : (
            <div className="saved-installation-items-table-wrap">
              <table className="items-subtable settlement-items-subtable">
                <thead>
                  <tr>
                    <th className="center-cell">#</th>
                    <th className="center-cell">Zakres</th>
                    <th className="center-cell">Rodzaj</th>
                    <th className="center-cell">Nazwa</th>
                    <th className="center-cell">Ilość</th>
                    <th className="center-cell">Obwód</th>
                    <th className="center-cell">Suma obwodów</th>
                    <th className="center-cell">USL</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((detailItem, index) => (
                    <tr key={detailItem.item_id}>
                      <td className="center-cell">{index + 1}</td>
                      <td className="center-cell">{detailItem.scope || '—'}</td>
                      <td className="center-cell">{detailItem.product_type || '—'}</td>
                      <td className="center-cell">{detailItem.name || '—'}</td>
                      <td className="center-cell">{detailItem.quantity ?? '—'}</td>
                      <td className="center-cell">{detailItem.perimeter ?? '—'}</td>
                      <td className="center-cell">{detailItem.perimeter_sum ?? '—'}</td>
                      <td className="center-cell">
                        {(detailItem.product_type || '').trim().toUpperCase() === 'USL'
                          ? (detailItem.net_price_pln ?? '—')
                          : '—'}
                      </td>
                    </tr>
                  ))}
                  <tr className="items-subtable-summary">
                    <td colSpan={4} className="center-cell"><strong>Suma</strong></td>
                    <td className="center-cell">
                      <strong>{items.reduce((sum, detailItem) => sum + Number(detailItem.quantity || 0), 0).toFixed(2)}</strong>
                    </td>
                    <td className="center-cell">
                      <strong>{items.reduce((sum, detailItem) => sum + Number(detailItem.perimeter || 0), 0).toFixed(2)}</strong>
                    </td>
                    <td className="center-cell">
                      <strong>{items.reduce((sum, detailItem) => sum + Number(detailItem.perimeter_sum || 0), 0).toFixed(2)}</strong>
                    </td>
                    <td className="center-cell"><strong>{totalUsl.toFixed(2)}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function SettlementCalculationEditor({ item, installer, onSave }) {
  const [overhead, setOverhead] = useState(String(item.overhead_percentage ?? 5));
  const [distribution, setDistribution] = useState(String(item.distribution_percentage ?? 25));
  const [useTargetAmount, setUseTargetAmount] = useState(false);
  const baseEarned = Number(item.earned || 0) - Number(item.additional_amount || 0);
  const [targetAmount, setTargetAmount] = useState(String(baseEarned));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setOverhead(String(item.overhead_percentage ?? 5));
    setDistribution(String(item.distribution_percentage ?? 25));
    setTargetAmount(String(Number(item.earned || 0) - Number(item.additional_amount || 0)));
  }, [item.overhead_percentage, item.distribution_percentage, item.earned, item.additional_amount]);

  function clearFeedback() {
    setError('');
    setSuccess('');
  }

  async function saveCalculation(saveGlobally) {
    const parsedOverhead = Number(String(overhead).replace(',', '.'));
    const parsedDistribution = Number(String(distribution).replace(',', '.'));
    const parsedTargetAmount = Number(String(targetAmount).replace(',', '.'));
    if (!Number.isFinite(parsedOverhead) || parsedOverhead < 0 || parsedOverhead > 100) {
      setError('Narzut musi mieścić się w zakresie od 0 do 100%.');
      return;
    }
    if (!Number.isFinite(parsedDistribution) || parsedDistribution < 0 || parsedDistribution > 100) {
      setError('Część do podziału musi mieścić się w zakresie od 0 do 100%.');
      return;
    }
    if (useTargetAmount && (!Number.isFinite(parsedTargetAmount) || parsedTargetAmount <= 0)) {
      setError('Kwota docelowa musi być większa od 0.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await onSave(item.installation_id, {
        overhead_percentage: parsedOverhead,
        distribution_percentage: parsedDistribution,
        target_installer_id: useTargetAmount ? installer.installer_id : undefined,
        target_amount: useTargetAmount ? parsedTargetAmount : undefined,
        save_globally: saveGlobally,
      });
      setUseTargetAmount(false);
      setSuccess(
        saveGlobally
          ? 'Zapisano w sesji oraz jako ustawienie tego zlecenia w systemie.'
          : 'Zapisano tylko w tej sesji rozliczeniowej.'
      );
    } catch (requestError) {
      setError(apiError(requestError, 'Nie udało się przeliczyć zlecenia.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="settlement-calculation-editor">
      <div className="settlement-calculation-editor-heading">
        <div className="settlement-calculation-editor-title">
          <span className="settlement-calculation-editor-icon" aria-hidden="true">
            <SlidersHorizontal size={18} />
          </span>
          <div>
            <span className="calculation-eyebrow">Edycja sposobu liczenia</span>
            <h5>Parametry zlecenia i kwota docelowa</h5>
            <p>Zmiana zostanie zastosowana do wszystkich pracowników tego zlecenia.</p>
          </div>
        </div>
        <div className="settlement-current-amount">
          <span>Aktualna kwota końcowa</span>
          <strong>{formatMoney(item.earned)}</strong>
          <small>{installer.full_name}</small>
        </div>
      </div>

      <div className="settlement-calculation-editor-body">
        <div className="settlement-calculation-group">
          <div className="settlement-calculation-group-heading">
            <span>1</span>
            <div>
              <strong>Parametry podziału</strong>
              <small>Wartości procentowe od 0 do 100</small>
            </div>
          </div>
          <div className="settlement-percentage-fields">
            <label>
              <span>Narzut</span>
              <span className="settlement-percentage-input">
                <input
                  type="text"
                  inputMode="decimal"
                  value={overhead}
                  onChange={(event) => {
                    setOverhead(event.target.value);
                    clearFeedback();
                  }}
                  disabled={saving}
                />
                <span>%</span>
              </span>
              <small>Odejmowany od sumy USL</small>
            </label>
            <label>
              <span>Część do podziału</span>
              <span className="settlement-percentage-input">
                <input
                  type="text"
                  inputMode="decimal"
                  value={distribution}
                  onChange={(event) => {
                    setDistribution(event.target.value);
                    clearFeedback();
                  }}
                  disabled={saving}
                />
                <span>%</span>
              </span>
              <small>Procent przekazywany do puli ekipy</small>
            </label>
          </div>
        </div>

        <div className={`settlement-calculation-group settlement-target-group${useTargetAmount ? ' is-active' : ''}`}>
          <div className="settlement-calculation-group-heading">
            <span>2</span>
            <div>
              <strong>Kwota docelowa pracownika</strong>
              <small>Opcjonalne dopasowanie wagi, bez dopłat końcowych</small>
            </div>
          </div>
          <div className="settlement-target-controls">
            <label className="settlement-target-switch">
              <input
                type="checkbox"
                checked={useTargetAmount}
                onChange={(event) => {
                  setUseTargetAmount(event.target.checked);
                  clearFeedback();
                }}
                disabled={saving}
              />
              <span className="settlement-target-switch-control" aria-hidden="true">
                <span />
              </span>
              <span className="settlement-target-switch-copy">
                <strong>Ustaw kwotę docelową</strong>
                <small>System przeliczy wagę i kwoty pozostałych osób.</small>
              </span>
            </label>
            <label className={`settlement-target-amount${useTargetAmount ? '' : ' is-disabled'}`}>
              <span>Kwota z podziału dla {installer.full_name}</span>
              <span className="pzl-money-input">
                <input
                  type="text"
                  inputMode="decimal"
                  value={targetAmount}
                  onChange={(event) => {
                    setTargetAmount(event.target.value);
                    clearFeedback();
                  }}
                  disabled={saving || !useTargetAmount}
                />
                <span>PLN</span>
              </span>
            </label>
          </div>
        </div>
      </div>

      {(error || success) && (
        <div className={`settlement-calculation-feedback${error ? ' is-error' : ' is-success'}`}>
          {error ? <X size={16} aria-hidden="true" /> : <Check size={16} aria-hidden="true" />}
          <span>{error || success}</span>
        </div>
      )}

      <div className="settlement-calculation-editor-footer">
        <div className="settlement-calculation-global-note">
          <Info size={16} aria-hidden="true" />
          <span>
            <strong>Zapis globalny</strong>
            utrwali parametry i wyliczone wagi dla tego zlecenia w kolejnych rozliczeniach.
          </span>
        </div>
        <div className="settlement-calculation-save-buttons">
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={saving}
            onClick={() => saveCalculation(false)}
          >
            <Save size={15} aria-hidden="true" />
            {saving ? 'Przeliczanie…' : 'Zapisz tylko w tej sesji'}
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={saving}
            onClick={() => saveCalculation(true)}
          >
            <Save size={15} aria-hidden="true" /> Zapisz również w systemie
          </button>
        </div>
      </div>
    </section>
  );
}

function UserDayFinalAmountEditor({ item, installer, day, onSave }) {
  const [amount, setAmount] = useState(String(day.additional_amount || 0));
  const [savingScope, setSavingScope] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const saving = savingScope !== null;

  useEffect(() => {
    setAmount(String(day.additional_amount || 0));
    setError('');
    setSuccess('');
  }, [day.additional_amount, day.assigned_date, day.team_id]);

  async function save(saveGlobally) {
    const parsedAmount = Number(String(amount).replace(',', '.'));
    if (!Number.isFinite(parsedAmount) || parsedAmount < 0) {
      setError('Wprowadź kwotę równą lub większą od 0.');
      return;
    }
    setSavingScope(saveGlobally ? 'system' : 'session');
    setError('');
    setSuccess('');
    try {
      await onSave(item.installation_id, {
        assigned_date: day.assigned_date,
        team_id: day.team_id ?? null,
        installer_amounts: { [installer.installer_id]: parsedAmount },
        save_globally: saveGlobally,
      });
      const scopeLabel = saveGlobally ? 'w systemie' : 'w tej sesji';
      setSuccess(
        parsedAmount > 0
          ? `Dopłata została zapisana ${scopeLabel}.`
          : `Dopłata została usunięta ${scopeLabel}.`
      );
    } catch (requestError) {
      setError(apiError(requestError, 'Nie udało się zapisać dopłaty.'));
    } finally {
      setSavingScope(null);
    }
  }

  function submit(event) {
    event.preventDefault();
    save(false);
  }

  return (
    <form className="day-final-amount-editor" onSubmit={submit}>
      <span className="day-final-assignment">
        <strong>{day.team_name || 'Przypisanie z tego dnia'}</strong>
        <small>Kwota końcowa dnia: {formatMoney(day.earned)}</small>
      </span>
      <label>
        <span>Dopłata do kwoty końcowej</span>
        <span className="pzl-money-input">
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(event) => {
              setAmount(event.target.value);
              setError('');
              setSuccess('');
            }}
            disabled={saving}
          />
          <span>PLN</span>
        </span>
      </label>
      <div className="day-final-amount-action">
        <button type="submit" className="btn-primary btn-sm" disabled={saving}>
          <Save size={14} aria-hidden="true" />
          {savingScope === 'session' ? 'Zapisywanie…' : 'Zapisz tylko w tej sesji'}
        </button>
        <button
          type="button"
          className="btn-secondary btn-sm"
          disabled={saving}
          onClick={() => save(true)}
        >
          <Save size={14} aria-hidden="true" />
          {savingScope === 'system' ? 'Zapisywanie…' : 'Zapisz w systemie'}
        </button>
      </div>
      {(error || success) && (
        <span className={`day-final-amount-feedback${error ? ' is-error' : ' is-success'}`}>
          {error || success}
        </span>
      )}
    </form>
  );
}

function UserAssignmentDaysFinalAmounts({ item, installer, onSave }) {
  if (!item.calculation_days?.length) return null;
  return (
    <section className="assignment-days-final-amounts">
      <strong>Dni przypisania ekipowego</strong>
      <span>
        Kwota zostanie dodana bez przeliczania USL. Zapis systemowy uwzględni ją
        również w kolejnych rozliczeniach.
      </span>
      {item.calculation_days.map((day, index) => (
        <div className="assignment-day-final-row" key={`${day.assigned_date || 'day'}:${day.team_id ?? index}`}>
          <strong>{day.assigned_date ? formatPolishDate(day.assigned_date) : `Dzień ${index + 1}`}</strong>
          <UserDayFinalAmountEditor item={item} installer={installer} day={day} onSave={onSave} />
        </div>
      ))}
    </section>
  );
}

function InstallationCalculation({ item, installer, isSnapshot, onUpdateTeamAdditionalAmount }) {
  if (isPzlItem(item) && item.pzl_settlement_mode && item.pzl_settlement_mode !== 'usl') {
    return (
      <section className="installation-calculation pzl-flat-calculation">
        <div className="installation-calculation-heading">
          <div>
            <span className="calculation-eyebrow">Cennik PZL</span>
            <h5>{pzlSettlementModeLabel(item.pzl_settlement_mode)}</h5>
          </div>
          <span className="calculation-position">
            {item.pzl_uses_price_list ? 'stawka z cennika' : 'stawka indywidualna'}
          </span>
        </div>
        <div className="employee-calculation-result">
          <div className="employee-calculation-meta">
            <span>Kwota cennikowa<strong>{formatMoney(item.pzl_settlement_amount)}</strong></span>
            <span>Kwota dla {installer.full_name}<strong>{formatMoney(item.earned)}</strong></span>
          </div>
          <p>
            {item.pzl_settlement_mode === 'per_person'
              ? 'Kwota przysługuje każdej osobie przypisanej do tego zlecenia.'
              : 'Łączna kwota została podzielona równo między osoby przypisane do zlecenia.'}
          </p>
        </div>
        {isSnapshot && (
          <UserAssignmentDaysFinalAmounts
            item={item}
            installer={installer}
            onSave={onUpdateTeamAdditionalAmount}
          />
        )}
      </section>
    );
  }

  const hasBaseCalculation = item.overhead_amount != null
    && item.base_amount != null
    && item.total_to_distribute != null;

  if (!hasBaseCalculation) {
    return (
      <div className="installation-calculation">
        <div className="installation-calculation is-unavailable">
          <Info size={17} aria-hidden="true" />
          <div>
            <strong>Szczegółowy sposób liczenia jest niedostępny</strong>
            <span>Ten raport zapisano przed dodaniem pełnego zapisu etapów obliczenia.</span>
          </div>
        </div>
        {isSnapshot && (
          <UserAssignmentDaysFinalAmounts
            item={item}
            installer={installer}
            onSave={onUpdateTeamAdditionalAmount}
          />
        )}
      </div>
    );
  }

  const isManual = item.settlement_mode === 'manual';

  return (
    <section className="installation-calculation" aria-label={`Wyliczenie dla ${installer.full_name}`}>
      <div className="installation-calculation-heading">
        <div>
          <span className="calculation-eyebrow">Sposób liczenia</span>
          <h5>Wyliczenie dla {installer.full_name}</h5>
        </div>
        <span className="calculation-position">{installer.position || 'Brak stanowiska'}</span>
      </div>

      <div className="calculation-flow">
        <div className="calculation-step">
          <span>1. Suma USL</span>
          <strong>{formatMoney(item.total_usl_value)}</strong>
        </div>
        <span className="calculation-operator">−</span>
        <div className="calculation-step">
          <span>2. Narzut ({formatNumber(item.overhead_percentage)}%)</span>
          <strong>{formatMoney(item.overhead_amount)}</strong>
        </div>
        <span className="calculation-operator">=</span>
        <div className="calculation-step is-highlighted">
          <span>3. Podstawa po narzucie</span>
          <strong>{formatMoney(item.base_amount)}</strong>
        </div>
        <span className="calculation-operator">×</span>
        <div className="calculation-step">
          <span>4. Część do podziału</span>
          <strong>{formatNumber(item.distribution_percentage)}%</strong>
        </div>
        <span className="calculation-operator">=</span>
        <div className="calculation-step is-highlighted">
          <span>5. Pula dla ekipy</span>
          <strong>{formatMoney(item.total_to_distribute)}</strong>
        </div>
      </div>

      {isManual ? (
        <div className="employee-calculation-result is-manual">
          <div>
            <span>Kwota przypisana ręcznie pracownikowi</span>
            <strong>{formatMoney(item.earned)}</strong>
          </div>
          <p>W tym sposobie rozliczenia waga stanowiska nie wpływa na końcową kwotę.</p>
        </div>
      ) : (
        <div className="employee-calculation-result">
          <div className="employee-calculation-meta">
            <span>
              Stanowisko
              <strong>{installer.position || 'Brak stanowiska'}</strong>
            </span>
            <span>
              Waga stanowiska
              <strong>{item.position_weight == null ? 'Brak danych' : formatNumber(item.position_weight)}</strong>
            </span>
            <span>
              Końcowy udział pracownika
              <strong>{formatNumber(item.percentage)}%</strong>
            </span>
          </div>
          {item.calculation_days?.length > 0 ? (
            <div className="calculation-days">
              <strong className="calculation-days-title">Podział według wystąpień (ekipa/dzień) i wag stanowisk</strong>
              {item.calculation_days.map((day, index) => (
                <div className="calculation-day-row" key={`${day.assigned_date || 'day'}-${index}`}>
                  <span className="calculation-day-date">
                    {day.assigned_date ? formatPolishDate(day.assigned_date) : `Dzień ${index + 1}`}
                  </span>
                  <span className="calculation-day-formula">
                    {day.team_weight > 0
                      ? `${formatMoney(day.day_pool)} × (${formatNumber(day.position_weight)} / ${formatNumber(day.team_weight)})`
                      : 'Brak skonfigurowanych wag dla ekipy'}
                  </span>
                  <span className="calculation-day-equals">=</span>
                  <strong>{formatMoney(day.earned)}</strong>
                  {Number(day.additional_amount || 0) > 0 && (
                    <span className="calculation-day-additional">
                      Kwota bazowa {formatMoney(Number(day.earned || 0) - Number(day.additional_amount || 0))}
                      {' '}+ dopłata {formatMoney(day.additional_amount)}
                    </span>
                  )}
                  {isSnapshot && (
                    <UserDayFinalAmountEditor
                      item={item}
                      installer={installer}
                      day={day}
                      onSave={onUpdateTeamAdditionalAmount}
                    />
                  )}
                </div>
              ))}
              <div className="employee-calculation-formula is-total">
                <span>
                  Suma kwot ze wszystkich wystąpień
                  {Number(item.additional_amount || 0) > 0
                    ? ` (w tym ${formatMoney(item.additional_amount)} dopłat)`
                    : ''}
                </span>
                <span>=</span>
                <strong>{formatMoney(item.earned)}</strong>
              </div>
            </div>
          ) : (
            <div className="employee-calculation-formula">
              <span>{formatMoney(item.total_to_distribute)}</span>
              <span>×</span>
              <span>{formatNumber(item.percentage)}%</span>
              <span>≈</span>
              <strong>{formatMoney(item.earned)}</strong>
            </div>
          )}
          <p>
            Pula dnia to pula zlecenia podzielona przez wszystkie dni jego realizacji.
            Raport sumuje tylko dni mieszczące się w wybranym okresie, a następnie
            dzieli pulę dnia według wag osób przypisanych tego dnia.
          </p>
        </div>
      )}
      {isSnapshot && isManual && (
        <UserAssignmentDaysFinalAmounts
          item={item}
          installer={installer}
          onSave={onUpdateTeamAdditionalAmount}
        />
      )}
    </section>
  );
}
